import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Package, DollarSign, Plus, Eye, Heart, ShoppingCart, Truck, MapPin, Shield, Crown, Headphones, CreditCard, Loader2, ShoppingBag, RefreshCw, AlertTriangle, Share2, Copy, Check, Warehouse, XCircle } from "lucide-react";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";
import { useNavigate, Link } from "react-router-dom";
import { EscrowDashboard } from "@/components/seller/EscrowDashboard";
import { SubscriptionPlans } from "@/components/seller/SubscriptionPlans";
import { SupportCenter } from "@/components/shared/SupportCenter";
import { SellerOrderManagement } from "@/components/seller/OrderManagement";
import { PickupLocationSettings } from "@/components/seller/PickupLocationSettings";
import { SelfDeliveryManagement } from "@/components/seller/SelfDeliveryManagement";
import { KYCSubmission } from "@/components/seller/KYCSubmission";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useDashboardData } from "@/hooks/useDashboardData";
import { toast } from "sonner";

export default function SellerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: dashboardData, loading: mainLoading, error, refetch } = useDashboardData(user?.id);
  
  const [productsLoading, setProductsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("escrow");
  const [copiedCatalog, setCopiedCatalog] = useState(false);
  const [businessName, setBusinessName] = useState<string>("");
  const [pickupMissing, setPickupMissing] = useState(false);
  
  const [topProducts, setTopProducts] = useState<any[]>([]);

  // Fetch business name and pickup status
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;
      const { data } = await supabase
        .from('profiles')
        .select('business_name, pickup_address_street, pickup_address_city, pickup_address_state, pickup_address_phone, delivery_preference')
        .eq('id', user.id)
        .single();
      if (data?.business_name) {
        setBusinessName(data.business_name);
      }
      if (data?.delivery_preference === 'partner') {
        const missing = !data.pickup_address_street || !data.pickup_address_city || !data.pickup_address_state || !data.pickup_address_phone;
        setPickupMissing(missing);
      }
    };
    fetchProfile();
  }, [user?.id]);

  // Compute stats from dashboard data
  const stats = useMemo(() => {
    if (!dashboardData) {
      return [
        { label: "Total Sales", value: "₦0", change: "0%", icon: DollarSign, color: "text-green-600" },
        { label: "Active Products", value: "0", change: "0", icon: Package, color: "text-blue-600" },
        { label: "Pending Balance", value: "₦0", change: "Awaiting confirmation", icon: Shield, color: "text-amber-600" },
        { label: "Available Balance", value: "₦0", change: "Ready for withdrawal", icon: TrendingUp, color: "text-emerald-600" },
      ];
    }
    
    const { products, orders, escrowAccount } = dashboardData;
    
    const activeProducts = products.filter(p => p.status === 'active').length;
    const totalSales = orders.reduce((sum, order) => sum + Number(order.total_amount), 0);
    
    // Escrow balances synced from escrow_accounts table
    const availableBalance = Number(escrowAccount?.balance_available || 0);
    const pendingBalance = Number(escrowAccount?.balance_pending || 0);
    
    return [
      { label: "Total Sales", value: `₦${totalSales.toLocaleString()}`, change: `${orders.length} orders`, icon: DollarSign, color: "text-green-600" },
      { label: "Active Products", value: activeProducts.toString(), change: `${products.length} total`, icon: Package, color: "text-blue-600" },
      { label: "Pending Balance", value: `₦${pendingBalance.toLocaleString()}`, change: "Awaiting confirmation", icon: Shield, color: "text-amber-600" },
      { label: "Available Balance", value: `₦${availableBalance.toLocaleString()}`, change: "Ready for withdrawal", icon: TrendingUp, color: "text-emerald-600" },
    ];
  }, [dashboardData]);


  // Process top products data
  useEffect(() => {
    if (!dashboardData?.products.length) {
      setTopProducts([]);
      setProductsLoading(false);
      return;
    }

    const processedProducts = dashboardData.products
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 3)
      .map(product => ({
        id: product.id,
        name: product.title,
        views: product.views || 0,
        likes: product.likes || 0,
        sales: dashboardData.orders.filter(o => {
          const items = o.items as any[];
          return items?.some(item => item.product_id === product.id);
        }).length
      }));
    
    setTopProducts(processedProducts);
    setProductsLoading(false);
  }, [dashboardData]);

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold">Seller Dashboard</h1>
              {pickupMissing && (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <XCircle className="h-3 w-3" />
                  Delivery Disabled
                </Badge>
              )}
            </div>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Welcome back! Here's your business overview
              {error && (
                <span className="flex items-center gap-1 text-destructive text-sm mt-1">
                  <AlertTriangle className="h-3 w-3" />
                  {error}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            {error && (
              <Button 
                variant="outline" 
                onClick={refetch} 
                disabled={mainLoading}
                className="flex items-center"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${mainLoading ? 'animate-spin' : ''}`} />
                Retry
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/seller/orders")} className="flex items-center">
              <ShoppingBag className="h-4 w-4 mr-2" />
              View Orders
            </Button>
            <Button variant="gradient" onClick={() => navigate("/seller/add-product")} className="flex items-center">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Fez Delivery Disabled Banner */}
        {pickupMissing && (
          <div className="mb-6 p-4 bg-destructive/10 border-2 border-destructive rounded-lg flex items-start gap-3">
            <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-bold text-destructive">Fez delivery disabled. Complete pickup location to enable.</p>
              <p className="text-sm text-destructive/80 mt-1">
                Your delivery preference has been set to self-delivery because your pickup address is incomplete.
                Buyers cannot select Fez shipping for your products until all fields are filled.
              </p>
              <Button
                variant="destructive"
                size="sm"
                className="mt-2"
                onClick={() => setActiveTab("shipping")}
              >
                <Warehouse className="h-4 w-4 mr-2" />
                Complete Pickup Location Now
              </Button>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        {mainLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-center h-20">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat) => (
              <Card key={stat.label}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.label}
                  </CardTitle>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="relative">
            {/* Scroll hint for mobile */}
            <p className="text-xs text-muted-foreground mb-2 sm:hidden flex items-center gap-1">
              <span>← Swipe to see more →</span>
            </p>
            <TabsList className="w-full gap-1 p-1.5 bg-muted/80 backdrop-blur-sm border border-border/50">
              <TabsTrigger value="escrow" className="text-xs sm:text-sm gap-1 sm:gap-2">
                <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>Escrow</span>
              </TabsTrigger>
              <TabsTrigger value="order-management" className="text-xs sm:text-sm gap-1 sm:gap-2">
                <ShoppingBag className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Manage Orders</span>
                <span className="sm:hidden">Orders</span>
              </TabsTrigger>
              <TabsTrigger value="products" className="text-xs sm:text-sm gap-1 sm:gap-2">
                <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Manage Products</span>
                <span className="sm:hidden">Products</span>
              </TabsTrigger>
              <TabsTrigger value="analytics" className="text-xs sm:text-sm">Analytics</TabsTrigger>
              <TabsTrigger value="subscription" className="text-xs sm:text-sm gap-1 sm:gap-2">
                <Crown className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Subscription</span>
                <span className="sm:hidden">Pro</span>
              </TabsTrigger>
              <TabsTrigger value="support" className="text-xs sm:text-sm gap-1 sm:gap-2">
                <Headphones className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>Support</span>
              </TabsTrigger>
              <TabsTrigger value="shipping" className="text-xs sm:text-sm gap-1 sm:gap-2">
                <Warehouse className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Shipping</span>
                <span className="sm:hidden">Ship</span>
              </TabsTrigger>
              <TabsTrigger value="deliveries" className="text-xs sm:text-sm gap-1 sm:gap-2">
                <Truck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Deliveries</span>
                <span className="sm:hidden">Deliver</span>
              </TabsTrigger>
              <TabsTrigger value="kyc" className="text-xs sm:text-sm gap-1 sm:gap-2">
                <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>KYC</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="escrow">
            <EscrowDashboard />
          </TabsContent>

          <TabsContent value="order-management">
            <SellerOrderManagement />
          </TabsContent>

          <TabsContent value="products">
            <Card>
              <CardHeader>
                <CardTitle>Manage Your Products</CardTitle>
                <CardDescription>View, edit, and manage all your products</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {productsLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : topProducts.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground mb-4">No products yet. Start by adding your first product!</p>
                      <Button onClick={() => navigate("/seller/add-product")} variant="gradient">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Your First Product
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-4">
                        {topProducts.map((product) => (
                          <div key={product.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/5 transition-colors">
                            <div className="flex-1">
                              <div className="font-medium">{product.name}</div>
                              <div className="flex gap-4 mt-2">
                                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <Eye className="h-3 w-3" /> {product.views} views
                                </span>
                                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <Heart className="h-3 w-3" /> {product.likes} likes
                                </span>
                                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <ShoppingCart className="h-3 w-3" /> {product.sales} sales
                                </span>
                              </div>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/seller/edit-product/${product.id}`)}
                            >
                              Edit
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-4">
                        <Button onClick={() => navigate("/seller/products")} className="flex-1">
                          <Package className="h-4 w-4 mr-2" />
                          View All Products
                        </Button>
                        <Button onClick={() => navigate("/seller/add-product")} variant="outline" className="flex-1">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Product
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Revenue Trends</CardTitle>
                  <CardDescription>Monthly revenue over the last 6 months</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { month: 'Jan', revenue: 45000, growth: '+12%' },
                      { month: 'Feb', revenue: 52000, growth: '+15%' },
                      { month: 'Mar', revenue: 48000, growth: '-8%' },
                      { month: 'Apr', revenue: 61000, growth: '+27%' },
                      { month: 'May', revenue: 58000, growth: '-5%' },
                      { month: 'Jun', revenue: 67000, growth: '+15%' }
                    ].map((data, index) => (
                      <div key={data.month} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <span className="font-medium">{data.month}</span>
                          <span className="text-sm text-muted-foreground ml-2">₦{data.revenue.toLocaleString()}</span>
                        </div>
                        <Badge variant={data.growth.startsWith('+') ? 'default' : 'destructive'}>
                          {data.growth}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Customer Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle>Customer Insights</CardTitle>
                  <CardDescription>Understanding your customer base</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 border rounded-lg">
                      <div className="text-2xl font-bold text-primary">{stats[2]?.value || '0'}</div>
                      <p className="text-sm text-muted-foreground">Total Customers</p>
                    </div>
                    <div className="text-center p-4 border rounded-lg">
                      <div className="text-2xl font-bold text-green-600">68%</div>
                      <p className="text-sm text-muted-foreground">Repeat Buyers</p>
                    </div>
                    <div className="text-center p-4 border rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">₦24,500</div>
                      <p className="text-sm text-muted-foreground">Avg Order Value</p>
                    </div>
                    <div className="text-center p-4 border rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">3.2</div>
                      <p className="text-sm text-muted-foreground">Orders per Customer</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Product Performance */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Product Performance</CardTitle>
                  <CardDescription>How your products are performing this month</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {topProducts.length > 0 ? topProducts.map((product, index) => (
                      <div key={product.name} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{product.name}</h4>
                          <Badge variant="outline">#{index + 1}</Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Views</span>
                            <span className="font-medium">{product.views}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Likes</span>
                            <span className="font-medium">{product.likes}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Sales</span>
                            <span className="font-medium">{product.sales}</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full" 
                              style={{ width: `${Math.min((product.views / 100) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="col-span-3 text-center py-8">
                        <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p className="text-muted-foreground">No products to analyze yet</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="subscription">
            <SubscriptionPlans />
          </TabsContent>
          
          <TabsContent value="support">
            <SupportCenter userType="seller" />
          </TabsContent>

          <TabsContent value="shipping">
            <PickupLocationSettings />
          </TabsContent>

          <TabsContent value="deliveries">
            <SelfDeliveryManagement />
          </TabsContent>

          <TabsContent value="kyc">
            <KYCSubmission />
          </TabsContent>
        </Tabs>

        {/* Quick Actions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/seller/bank-accounts">
                <Button variant="outline" size="sm">
                  <CreditCard className="mr-2 h-4 w-4" />
                  Manage Bank Accounts
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Share Catalog Card */}
        <Card className="mt-8 bg-gradient-whatsapp text-whatsapp-foreground overflow-hidden">
          <CardHeader>
            <div className="flex items-center gap-2">
              <WhatsAppIcon className="h-6 w-6" size={24} />
              <CardTitle className="text-whatsapp-foreground">Share Your Catalog on WhatsApp</CardTitle>
            </div>
            <CardDescription className="text-whatsapp-foreground/80">
              Boost your sales by sharing all your products with customers
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Catalog Link Display */}
              {dashboardData?.products && dashboardData.products.length > 0 && (
                <div className="bg-white/20 rounded-lg p-3">
                  <p className="text-xs text-whatsapp-foreground/70 mb-1">Your catalog link:</p>
                  <code className="text-sm break-all">
                    {`${window.location.origin}/sellers?seller=${user?.id}`}
                  </code>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="glass" 
                  className="flex-1"
                  onClick={() => {
                    if (!dashboardData?.products?.length) {
                      toast.error("Add some products first to share your catalog");
                      return;
                    }
                    
                    // Use OG storefront link for rich preview with product image
                    const ogStorefrontLink = `https://fdcqcibrdsuvmdwagulk.supabase.co/functions/v1/og-storefront?seller=${user?.id}`;
                    let message = `${ogStorefrontLink}\n\n`;
                    message += `🛍️ *${businessName || 'My Store'} - Product Catalog*\n\n`;
                    message += `📦 ${dashboardData.products.length} Products Available\n\n`;
                    
                    // List top 5 products
                    const productList = dashboardData.products.slice(0, 5).map((p, i) => 
                      `${i + 1}. ${p.title} - ₦${p.price.toLocaleString()}`
                    ).join('\n');
                    message += productList;
                    
                    if (dashboardData.products.length > 5) {
                      message += `\n...and ${dashboardData.products.length - 5} more products!`;
                    }
                    
                    message += `\n\n✅ Secure Payment | 🚚 Fast Delivery`;
                    message += `\n\n👆 Click the link to view all products!`;
                    
                    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, '_blank');
                    toast.success('Opening WhatsApp to share your catalog!');
                  }}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share All Products
                </Button>
                
                <Button 
                  variant="glass" 
                  className="flex-1"
                  onClick={async () => {
                    if (!dashboardData?.products?.length) {
                      toast.error("Add some products first");
                      return;
                    }
                    
                    // Use OG storefront link for rich preview with product image
                    const ogStorefrontLink = `https://fdcqcibrdsuvmdwagulk.supabase.co/functions/v1/og-storefront?seller=${user?.id}`;
                    let message = `${ogStorefrontLink}\n\n`;
                    message += `🛍️ *${businessName || 'My Store'} - Product Catalog*\n\n`;
                    message += `📦 ${dashboardData.products.length} Products Available\n`;
                    message += `\n#SabiTradeNG #OnlineShopping`;
                    
                    await navigator.clipboard.writeText(message);
                    setCopiedCatalog(true);
                    toast.success('Catalog link copied! Paste it on your WhatsApp Status');
                    setTimeout(() => setCopiedCatalog(false), 3000);
                  }}
                >
                  {copiedCatalog ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy for Status
                    </>
                  )}
                </Button>
              </div>
              
              <div className="flex justify-between items-center pt-2 border-t border-white/20">
                <div className="text-sm">
                  <span className="opacity-80">Products ready to share: </span>
                  <span className="font-bold">{dashboardData?.products?.length || 0}</span>
                </div>
                <Button variant="link" className="text-whatsapp-foreground p-0 h-auto" onClick={() => navigate("/seller/whatsapp-settings")}>
                  WhatsApp Settings →
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}